/**
 * WavePickupManager - Handles pickup spawning, animation, and collection
 *
 * Pickups appear on the road at milestone markers. When the player
 * reaches the milestone, the pickup is collected with a particle effect.
 *
 * Supports multiple pickup types via PickupRegistry:
 * - 'wave' - Item packs that go to inventory (default)
 * - Additional types can be registered for different behaviors
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { pickupRegistry } from '../registries/PickupRegistry.js';
import { registerDefaultPickupTypes, getQualityParticleColor } from './pickups/PickupTypes.js';

export class WavePickupManager {
    constructor(scene, camera, biomeManager, gameState, worldTranslator, sfxManager = null, preloader = null) {
        this.scene = scene;
        this.camera = camera;
        this.biomeManager = biomeManager;
        this.gameState = gameState;
        this.worldTranslator = worldTranslator;
        this.sfxManager = sfxManager;

        // Self-register for preload tracking with dispose callback
        this._onPreloadComplete = preloader?.register('wavePickups', {
            dispose: () => this.dispose()
        });

        // Active pickups in the world
        this.pickups = [];

        // Loaded wave models (cached)
        this.waveModels = [];
        this.modelsLoaded = false;

        // Particle systems for explosions
        this.activeParticles = [];

        // Sound
        this.pickupSound = null;
        this.soundLoaded = false;

        // Quality to color mapping
        this.qualityColors = {
            faded: 0x888888,      // gray
            standard: 0x4a9eff,   // blue
            pristine: 0xa335ee,   // purple
            golden: 0xff8000,     // orange
            vectorized: 0xe6cc80  // gold
        };

        // Game units per mile (from GameState)
        this.gameUnitsPerMile = 450;

        // Model transform configuration (adjust these to tweak appearance)
        this.modelConfig = {
            // Single model to use (set to null to use manifest.json list)
            singleModel: 'cassette.fbx',
            // Scale multiplier (uniform scale)
            scale: 0.03,
            // Rotation in degrees (applied as Euler angles)
            rotation: { x: 0, y: 0, z: -90 },
            // Position offset from road placement (local to pickup)
            offset: { x: 0, y: 0, z: 0 },
            // Height above road surface
            floatHeight: 2
        };

        // Callbacks
        this.onPickupCollected = null;  // For non-autoCollect types (waves)
        this.onAutoCollect = null;      // For autoCollect types (salvage crates)

        // Disposal state
        this._disposed = false;
    }

    /**
     * Initialize the manager - load models, sounds, and register pickup types
     */
    async initialize() {
        // Register default pickup types if not already registered
        registerDefaultPickupTypes(pickupRegistry);

        await this.loadWaveModels();
        this.loadPickupSound();
        this._onPreloadComplete?.();
    }

    /**
     * Load wave models from /assets/wavemodels/ folder
     * If modelConfig.singleModel is set, only loads that model
     * Otherwise uses manifest.json to list available models
     * Supports .glb, .gltf, and .fbx formats
     * Falls back to cube if no models found
     */
    async loadWaveModels() {
        const gltfLoader = new GLTFLoader();
        const fbxLoader = new FBXLoader();
        const modelPaths = [];

        // Check if we should use a single model
        if (this.modelConfig.singleModel) {
            modelPaths.push(`/assets/wavemodels/${this.modelConfig.singleModel}`);
        } else {
            // Try to fetch manifest file listing available models
            try {
                const response = await fetch('/assets/wavemodels/manifest.json');
                if (response.ok) {
                    const filenames = await response.json();
                    for (const filename of filenames) {
                        modelPaths.push(`/assets/wavemodels/${filename}`);
                    }
                }
            } catch (e) {
                console.log('Could not fetch wavemodels manifest, using fallback cube');
            }
        }

        // Load each model with appropriate loader
        for (const path of modelPaths) {
            try {
                const isFbx = path.toLowerCase().endsWith('.fbx');
                let model;

                if (isFbx) {
                    model = await new Promise((resolve, reject) => {
                        fbxLoader.load(path, resolve, undefined, reject);
                    });
                } else {
                    const gltf = await new Promise((resolve, reject) => {
                        gltfLoader.load(path, resolve, undefined, reject);
                    });
                    model = gltf.scene.clone();
                }

                this.waveModels.push(model);
                console.log(`Loaded wave model: ${path}`);
            } catch (e) {
                console.warn(`Failed to load wave model: ${path}`, e);
            }
        }

        this.modelsLoaded = true;
        console.log(`Loaded ${this.waveModels.length} wave models`);
    }

    /**
     * Load pickup sound effect
     */
    loadPickupSound() {
        try {
            this.pickupSound = new Audio('/assets/sfx/wavepickup.wav');
            this.pickupSound.volume = 0.5;
            this.pickupSound.addEventListener('canplaythrough', () => {
                this.soundLoaded = true;
                // Register with SFXManager if available
                if (this.sfxManager) {
                    this.sfxManager.registerSound('wavePickup', this.pickupSound);
                }
            });
            this.pickupSound.addEventListener('error', () => {
                console.log('Wave pickup sound not found, continuing without sound');
                this.pickupSound = null;
            });
        } catch (e) {
            console.log('Could not load wave pickup sound:', e);
            this.pickupSound = null;
        }
    }

    /**
     * Get color based on pack quality
     */
    getQualityColor(packQuality) {
        if (packQuality < 0.2) return this.qualityColors.faded;
        if (packQuality < 0.4) return this.qualityColors.standard;
        if (packQuality < 0.6) return this.qualityColors.pristine;
        if (packQuality < 0.8) return this.qualityColors.golden;
        return this.qualityColors.vectorized;
    }

    /**
     * Create a pickup mesh based on pickup type
     * @param {number} color - Hex color for the mesh
     * @param {string} pickupType - Type of pickup ('wave' or 'salvage-crate')
     * @returns {THREE.Object3D} The pickup mesh
     */
    createPickupMesh(color, pickupType = 'wave') {
        let mesh;
        const config = this.modelConfig;

        // Salvage crates use a distinct box/crate geometry
        if (pickupType === 'salvage-crate') {
            return this.createSalvageCrateMesh(color);
        }

        // Wave pickups use the loaded cassette model or fallback cube
        if (this.waveModels.length > 0) {
            // Use random model from loaded models
            const modelIndex = Math.floor(Math.random() * this.waveModels.length);
            mesh = this.waveModels[modelIndex].clone();

            // Apply scale
            mesh.scale.setScalar(config.scale);

            // Apply rotation (convert degrees to radians)
            mesh.rotation.set(
                THREE.MathUtils.degToRad(config.rotation.x),
                THREE.MathUtils.degToRad(config.rotation.y),
                THREE.MathUtils.degToRad(config.rotation.z)
            );

            // Apply color to all meshes in the model
            mesh.traverse((child) => {
                if (child.isMesh) {
                    // Handle both single material and material arrays
                    if (Array.isArray(child.material)) {
                        child.material = child.material.map(mat => {
                            const cloned = mat.clone();
                            cloned.color.setHex(color);
                            cloned.emissive = new THREE.Color(color);
                            cloned.emissiveIntensity = 0.3;
                            return cloned;
                        });
                    } else if (child.material && child.material.clone) {
                        child.material = child.material.clone();
                        child.material.color.setHex(color);
                        child.material.emissive = new THREE.Color(color);
                        child.material.emissiveIntensity = 0.3;
                    }
                }
            });
        } else {
            // Fallback: 1x1x1 cube
            const geometry = new THREE.BoxGeometry(1, 1, 1);
            const material = new THREE.MeshStandardMaterial({
                color: color,
                emissive: color,
                emissiveIntensity: 0.3,
                metalness: 0.5,
                roughness: 0.3
            });
            mesh = new THREE.Mesh(geometry, material);
        }

        return mesh;
    }

    /**
     * Create a salvage crate mesh - distinct box/crate shape
     * @param {number} color - Hex color (usually teal 0x44aa88)
     * @returns {THREE.Group} The crate mesh group
     */
    createSalvageCrateMesh(color) {
        const group = new THREE.Group();

        // Main crate body - slightly flattened box
        const crateGeometry = new THREE.BoxGeometry(1.2, 0.8, 0.8);
        const crateMaterial = new THREE.MeshStandardMaterial({
            color: color,
            emissive: color,
            emissiveIntensity: 0.4,
            metalness: 0.6,
            roughness: 0.4
        });
        const crate = new THREE.Mesh(crateGeometry, crateMaterial);
        group.add(crate);

        // Decorative straps/bands on the crate
        const strapMaterial = new THREE.MeshStandardMaterial({
            color: 0x225544,  // Darker teal for straps
            emissive: 0x225544,
            emissiveIntensity: 0.2,
            metalness: 0.7,
            roughness: 0.3
        });

        // Horizontal strap
        const hStrapGeometry = new THREE.BoxGeometry(1.25, 0.1, 0.85);
        const hStrap = new THREE.Mesh(hStrapGeometry, strapMaterial);
        hStrap.position.y = 0;
        group.add(hStrap);

        // Vertical strap
        const vStrapGeometry = new THREE.BoxGeometry(0.1, 0.85, 0.85);
        const vStrap = new THREE.Mesh(vStrapGeometry, strapMaterial);
        vStrap.position.x = 0;
        group.add(vStrap);

        // Scale to match wave pickup size approximately
        group.scale.setScalar(1.0);

        return group;
    }

    /**
     * Spawn a pickup at a specific milestone
     * @param {Object} packData - Data about the pickup (milestone, packQuality, cost, etc.)
     * @param {string} [pickupType='wave'] - Type ID from PickupRegistry
     * @returns {Object|null} The spawned pickup object, or null if failed
     */
    spawnPickup(packData, pickupType = 'wave') {
        const milestone = packData.milestone;
        const quality = packData.packQuality;

        // Look up type config from registry
        let typeConfig = null;
        if (pickupRegistry.has(pickupType)) {
            typeConfig = pickupRegistry.get(pickupType);
        }

        // Use type's particleColor if specified, otherwise quality-based
        const color = (typeConfig?.particleColor != null)
            ? typeConfig.particleColor
            : this.getQualityColor(quality);

        // Calculate relative distance ahead of camera
        const currentMiles = this.gameState.getTotalMiles();
        const milesAhead = milestone - currentMiles;

        // If the milestone is behind us, don't spawn (caller should roll a new target)
        if (milesAhead <= 0) {
            return null;
        }

        // Get current world offset to properly position pickup
        // The world moves via worldTranslator, so we need to account for this
        const worldOffset = this.worldTranslator ? this.worldTranslator.getGroupOffset() : 0;

        // Calculate local Z position: ahead of camera in world-translated space
        // Local Z = -(distance ahead) - worldOffset to place it ahead of camera
        const distanceAhead = milesAhead * this.gameUnitsPerMile;
        const targetZ = -distanceAhead - worldOffset;

        // Get road position at this Z (use getRoadDataAtZ for proper right vector)
        const biome = this.biomeManager?.currentBiome;
        if (!biome) {
            console.warn('No biome available for wave pickup placement');
            return null;
        }

        // getRoadDataAtZ returns position, tangent, AND right vector for curved roads
        const roadData = biome.getRoadDataAtZ(targetZ);
        if (!roadData) {
            console.warn('Could not get road position for wave pickup');
            return null;
        }

        // Create the pickup mesh (after all validations pass)
        // Pass pickupType to create appropriate visual (cassette vs crate)
        const mesh = this.createPickupMesh(color, pickupType);

        // Position on right lane (offset by 1/4 road width to the right)
        const roadWidth = biome.roadWidth || 10;
        const laneOffset = roadWidth * 0.25;
        const config = this.modelConfig;

        const position = roadData.position.clone();
        position.addScaledVector(roadData.right, laneOffset);
        position.y += config.floatHeight; // Float above road

        // Apply model offset
        position.x += config.offset.x;
        position.y += config.offset.y;
        position.z += config.offset.z;

        mesh.position.copy(position);

        // Store pickup data with type information
        const pickup = {
            mesh: mesh,
            milestone: milestone,
            packData: packData,
            pickupType: pickupType,
            baseY: position.y,
            color: color,
            collected: false,
            spawnTime: performance.now()
        };

        // Add to scene via worldTranslator if available
        if (this.worldTranslator) {
            this.worldTranslator.add(mesh);
        } else {
            this.scene.add(mesh);
        }

        this.pickups.push(pickup);

        return pickup;
    }

    /**
     * Spawn a test pickup at a fixed distance ahead of camera (for debugging)
     * @param {number} unitsAhead - Distance in game units ahead of camera (default 100)
     */
    spawnTestPickup(unitsAhead = 100) {
        // Pick a random quality for visual variety
        const qualities = ['faded', 'standard', 'pristine', 'golden', 'vectorized'];
        const quality = qualities[Math.floor(Math.random() * qualities.length)];
        const color = this.qualityColors[quality];

        // Create the pickup mesh
        const mesh = this.createPickupMesh(color);

        // Get current world offset
        const worldOffset = this.worldTranslator ? this.worldTranslator.getGroupOffset() : 0;

        // Calculate target Z position ahead of camera
        const targetZ = -unitsAhead - worldOffset;

        // Get road position at this Z
        const biome = this.biomeManager?.currentBiome;
        if (!biome) {
            console.warn('No biome available for test pickup placement');
            return null;
        }

        const roadData = biome.getRoadDataAtZ(targetZ);
        if (!roadData) {
            console.warn('Could not get road position for test pickup');
            return null;
        }

        // Position on right lane
        const roadWidth = biome.roadWidth || 10;
        const laneOffset = roadWidth * 0.25;
        const config = this.modelConfig;

        const position = roadData.position.clone();
        position.addScaledVector(roadData.right, laneOffset);
        position.y += config.floatHeight; // Float above road

        // Apply model offset
        position.x += config.offset.x;
        position.y += config.offset.y;
        position.z += config.offset.z;

        mesh.position.copy(position);

        // Store pickup data (no packData, just for visual testing)
        const pickup = {
            mesh: mesh,
            milestone: null,
            packData: null,
            baseY: position.y,
            color: color,
            collected: false,
            spawnTime: performance.now(),
            isTestPickup: true
        };

        // Add to scene
        if (this.worldTranslator) {
            this.worldTranslator.add(mesh);
        } else {
            this.scene.add(mesh);
        }

        this.pickups.push(pickup);

        return pickup;
    }

    /**
     * Create particle explosion effect attached to camera
     * Particles start in front of camera and explode outward into the environment
     */
    createParticleExplosion(color) {
        const particleCount = 100;
        const positions = new Float32Array(particleCount * 3);
        const velocities = [];
        const colors = new Float32Array(particleCount * 3);

        const colorObj = new THREE.Color(color);

        // Particles start at camera-relative position (0, 0, -1) - just in front
        for (let i = 0; i < particleCount; i++) {
            // Start position: slightly in front of camera with small random spread
            positions[i * 3] = (Math.random() - 0.5) * 0.5;     // X: small spread
            positions[i * 3 + 1] = (Math.random() - 0.5) * 0.5; // Y: small spread
            positions[i * 3 + 2] = -1 - Math.random() * 0.5;    // Z: just in front of camera

            // Explode outward into environment (negative Z = forward, spread in X/Y)
            const angle = (i / particleCount) * Math.PI * 2;
            const forwardSpeed = 20 + Math.random() * 30;  // Strong forward momentum
            const spreadSpeed = 5 + Math.random() * 15;    // Lateral spread

            velocities.push({
                x: Math.cos(angle) * spreadSpeed,
                y: Math.sin(angle) * spreadSpeed * 0.5 + Math.random() * 5, // Slight upward bias
                z: -forwardSpeed  // Explode forward (into the scene)
            });

            // Vary color slightly
            const variance = 0.8 + Math.random() * 0.4;
            colors[i * 3] = colorObj.r * variance;
            colors[i * 3 + 1] = colorObj.g * variance;
            colors[i * 3 + 2] = colorObj.b * variance;
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        const material = new THREE.PointsMaterial({
            size: 4,
            vertexColors: true,
            transparent: true,
            opacity: 1,
            sizeAttenuation: true,
            depthWrite: false
        });

        const particles = new THREE.Points(geometry, material);

        // Add as child of camera so it moves with camera view
        this.camera.add(particles);

        const particleSystem = {
            mesh: particles,
            velocities: velocities,
            startTime: performance.now(),
            duration: 2000, // 2 seconds
            geometry: geometry,
            isChildOfCamera: true  // Flag for cleanup
        };

        this.activeParticles.push(particleSystem);

        return particleSystem;
    }

    /**
     * Play pickup sound
     */
    playPickupSound() {
        this.sfxManager?.playSound('wavePickup');
    }

    /**
     * Collect a pickup - trigger effects and type-specific behavior
     */
    collectPickup(pickup) {
        if (pickup.collected) return;

        pickup.collected = true;

        // Create particle explosion in front of camera
        this.createParticleExplosion(pickup.color);

        // Play sound
        this.playPickupSound();

        // Remove pickup mesh from scene
        if (this.worldTranslator) {
            this.worldTranslator.remove(pickup.mesh);
        } else {
            this.scene.remove(pickup.mesh);
        }

        // Dispose of mesh resources
        pickup.mesh.traverse((child) => {
            if (child.isMesh) {
                child.geometry?.dispose();
                // Handle both single material and material arrays
                if (Array.isArray(child.material)) {
                    child.material.forEach(mat => mat?.dispose());
                } else {
                    child.material?.dispose();
                }
            }
        });

        // Skip callbacks for test pickups which have no packData
        if (!pickup.packData) {
            return;
        }

        // Look up type config from registry
        // Handle legacy saves where pickupType may be object instead of string
        const rawPickupType = pickup.pickupType;
        const pickupType = (typeof rawPickupType === 'object' && rawPickupType?.id)
            ? rawPickupType.id
            : (rawPickupType || 'wave');
        let typeConfig = null;
        if (pickupRegistry.has(pickupType)) {
            typeConfig = pickupRegistry.get(pickupType);
        }

        // Execute type's onCollect callback if defined
        let collectResult = null;
        if (typeConfig?.onCollect) {
            collectResult = typeConfig.onCollect(this.gameState, pickup.packData);
        }

        // Handle based on autoCollect setting
        if (typeConfig?.autoCollect) {
            // Auto-collect pickups (like salvage crates):
            // - Award happens in onCollect
            // - Trigger auto-collect callback with result for popup display
            // - Do NOT add to inventory or trigger legacy callback
            if (this.onAutoCollect) {
                this.onAutoCollect(pickupType, collectResult, pickup.packData);
            }
        } else {
            // Non-auto-collect pickups (like waves):
            // - Go to inventory via legacy callback
            // - Caller shows event popup
            if (this.onPickupCollected) {
                this.onPickupCollected(pickup.packData);
            }
        }
    }

    /**
     * Update all pickups and particles
     * Call this every frame
     */
    update(deltaTime) {
        if (this._disposed) return;

        const time = performance.now() / 1000;

        // Get world group offset for position-based collection
        const worldOffset = this.worldTranslator ? this.worldTranslator.getGroupOffset() : 0;

        // Update pickups
        for (let i = this.pickups.length - 1; i >= 0; i--) {
            const pickup = this.pickups[i];

            if (pickup.collected) {
                // Remove from array
                this.pickups.splice(i, 1);
                continue;
            }

            // Check if player has physically reached the pickup
            // Trigger collection 2 units ahead of camera to prevent clipping into car
            const pickupWorldZ = pickup.mesh.position.z + worldOffset;
            if (pickupWorldZ >= -2) {
                this.collectPickup(pickup);
                continue;
            }

            // Animate: bounce up and down
            const bounceOffset = Math.sin(time * 3) * 0.3;
            pickup.mesh.position.y = pickup.baseY + bounceOffset;

            // Animate: slow rotation
            pickup.mesh.rotation.y += deltaTime * 1;
        }

        // Update particle systems
        for (let i = this.activeParticles.length - 1; i >= 0; i--) {
            const ps = this.activeParticles[i];
            const elapsed = performance.now() - ps.startTime;
            const progress = elapsed / ps.duration;

            if (progress >= 1) {
                // Remove expired particle system from camera or scene
                if (ps.isChildOfCamera) {
                    this.camera.remove(ps.mesh);
                } else {
                    this.scene.remove(ps.mesh);
                }
                ps.geometry.dispose();
                ps.mesh.material.dispose();
                this.activeParticles.splice(i, 1);
                continue;
            }

            // Update particle positions
            const positions = ps.geometry.attributes.position.array;
            const dt = deltaTime;

            for (let j = 0; j < ps.velocities.length; j++) {
                const vel = ps.velocities[j];

                // Apply velocity to position (no gravity for camera-relative particles)
                positions[j * 3] += vel.x * dt;
                positions[j * 3 + 1] += vel.y * dt;
                positions[j * 3 + 2] += vel.z * dt;

                // Slight dampening
                vel.x *= 0.99;
                vel.y *= 0.99;
                vel.z *= 0.98;
            }

            ps.geometry.attributes.position.needsUpdate = true;

            // Fade out
            ps.mesh.material.opacity = 1 - progress;
        }
    }

    /**
     * Check if a pickup exists for a given milestone
     */
    hasPickupAtMilestone(milestone) {
        return this.pickups.some(p => p.milestone === milestone && !p.collected);
    }

    /**
     * Dispose of all resources
     */
    dispose() {
        if (this._disposed) return;
        this._disposed = true;

        // Remove all pickups
        for (const pickup of this.pickups) {
            if (this.worldTranslator) {
                this.worldTranslator.remove(pickup.mesh);
            } else {
                this.scene.remove(pickup.mesh);
            }
            pickup.mesh.traverse((child) => {
                if (child.isMesh) {
                    child.geometry?.dispose();
                    child.material?.dispose();
                }
            });
        }
        this.pickups = [];

        // Remove all particles
        for (const ps of this.activeParticles) {
            if (ps.isChildOfCamera) {
                this.camera.remove(ps.mesh);
            } else {
                this.scene.remove(ps.mesh);
            }
            ps.geometry.dispose();
            ps.mesh.material.dispose();
        }
        this.activeParticles = [];

        // Clear cached models
        this.waveModels = [];
    }
}
